import { ECustomThemeViews } from '@olo-web/types/enums';
import { EFirestoreCollections } from './../../../types/enums/firestoreCollections.enum';
import { getMerchantThemeCollectionName } from '@olo-web/utils/common/functions/getMerchantThemeCollectionName';
import { FirebaseError } from 'firebase-admin';
import { firestore } from '../../../utils/google/firebase';
import { useMutation, UseMutationOptions, useQueryClient } from 'react-query';
import { IMerchantTheme } from '@olo-web/types/merchantTheme.interface';
import { useMerchantIdentification } from '../queries/useMerchantIdentification';
import { useRouter } from 'next/router';
import { useMerchantTheme } from '../queries/useMerchantTheme';

type T = {
  theme: IMerchantTheme;
  docId?: string; // optional since well find docs by merchant ids (could actaully be removed)
  merchantIdentification?: any;
  currentTheme?: IMerchantTheme;
  // userId: string; // a reminder to add an updatedBy
};

export const updateMerchantTheme = async ({
  theme,
  merchantIdentification,
  currentTheme,
}: T): Promise<void> => {
  try {
    // get merchantIds from theme if exist
    const { merchantIds: initialMerchantIds, ...themeWithoutMerchantIds } = theme;
    let merchantIds = initialMerchantIds;

    // if merchantIds are missing, get them from context
    if (!merchantIds || merchantIds.length === 0) {
      const derivedMerchantIds = [];

      if (merchantIdentification?.merchant_id) {
        derivedMerchantIds.push(merchantIdentification.merchant_id);
      }

      if (
        merchantIdentification?.business_location_id &&
        !derivedMerchantIds.includes(merchantIdentification.business_location_id)
      ) {
        derivedMerchantIds.push(merchantIdentification.business_location_id);
      }

      // catch-all for merchantIds from currentTheme that arent already in array
      if (currentTheme?.merchantIds?.length) {
        currentTheme.merchantIds.forEach((id) => {
          if (!derivedMerchantIds.includes(id)) {
            derivedMerchantIds.push(id);
          }
        });
      }

      merchantIds = derivedMerchantIds;
    }

    if (!merchantIds || merchantIds.length === 0) {
      throw new Error('No merchant IDs provided for theme update');
    }

    // find documents that contain any merchantIds
    const querySnapshot = await firestore()
      .collection(getMerchantThemeCollectionName())
      .where('merchantIds', 'array-contains-any', merchantIds)
      .get();

    if (querySnapshot.empty) {
      throw new Error('No matching theme documents found');
    }

    // update each matching document
    const batch = firestore().batch();
    querySnapshot.docs.forEach((doc) => {
      batch.set(doc.ref, themeWithoutMerchantIds, { merge: true });
    });

    // apply updates to all documents
    await batch.commit();
    return;
  } catch (error) {
    throw new Error(error?.message || error);
  }
};

export const useUpdateMerchantTheme = (options?: UseMutationOptions<void, FirebaseError, T>) => {
  const queryClient = useQueryClient();
  const router = useRouter();
  const { merchantId } = router.query;
  const { data: merchantIdentification } = useMerchantIdentification(merchantId as string);
  const { data: currentTheme } = useMerchantTheme();

  // instead of passing updateMerchantTheme, we need to supply the merchantIds to the mutation because
  // the merchantIds are not available in the useMutation hook
  return useMutation(
    ({ theme }: Omit<T, 'merchantIdentification' | 'currentTheme'>) =>
      updateMerchantTheme({ theme, merchantIdentification, currentTheme }),
    {
      ...options,
      onMutate: (variables) => {
        if (merchantIdentification) {
          const merchantIds = [];

          if (merchantIdentification.merchant_id) {
            merchantIds.push(merchantIdentification.merchant_id);
          }

          if (
            merchantIdentification.business_location_id &&
            !merchantIds.includes(merchantIdentification.business_location_id)
          ) {
            merchantIds.push(merchantIdentification.business_location_id);
          }

          // catch-all for merchantIds from currentTheme that arent already in array
          if (currentTheme?.merchantIds?.length) {
            currentTheme.merchantIds.forEach((id) => {
              if (!merchantIds.includes(id)) {
                merchantIds.push(id);
              }
            });
          }

          // consolidate merchantIds and theme updates
          if (merchantIds.length > 0) {
            variables = {
              ...variables,
              theme: {
                ...variables.theme,
                merchantIds,
              },
            };
          }
        }

        if (options?.onMutate) {
          return options.onMutate(variables);
        }

        return variables;
      },
      onSuccess: (_, variables, context) => {
        const { theme } = variables;
        queryClient.refetchQueries(EFirestoreCollections.MERCHANT_THEME);
        if (ECustomThemeViews.BACKGROUND_IMAGE in theme || ECustomThemeViews.LOGO in theme) {
          queryClient.refetchQueries(['previous-images']);
        }
        if (options?.onSuccess) options.onSuccess(_, variables, context);
      },
    }
  );
};
